import React from 'react';
import classNames from 'classnames';
import { getRegion } from 'utils/Region';
import styles from './MatchCard.module.scss';
import PlaceholderBandera from 'containers/Mundial2026/assets/placeholders/Bandera.png';
import PlaceholderEscudo from 'containers/Mundial2026/assets/placeholders/Escudo_transparente.png';

const STATUS = {
  COMPLETED: 'COMPLETED',
  IN_PROGRESS: 'IN_PROGRESS',
  SCHEDULED: 'SCHEDULED',
};

const UNDEFINED_BAR_COLOR = 'rgba(255, 255, 255, 0.2)';

const formatTime = displayTime => {
  if (!displayTime || getRegion() !== 'dominicana') return displayTime;
  const parts = displayTime.split(':');
  if (parts.length < 2) return displayTime;
  const h = parseInt(parts[0], 10);
  const m = parts[1];
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${m} ${period}`;
};

const getResultLetter = ({ isWinner, isDraw, isLose }) => {
  if (isWinner) return 'G';
  if (isDraw) return 'E';
  if (isLose) return 'P';
  return null;
};

const getResultVariant = ({ isWinner, isDraw, isLose }) => {
  if (isWinner) return 'win';
  if (isDraw) return 'draw';
  if (isLose) return 'loss';
  return null;
};

const formatLiveTime = matchProgress => {
  if (!matchProgress) return '–';
  const { minute = null, second = null, hour=null } = matchProgress;
  
  const ss = second !== null ? `${String(second).padStart(2, '0')}`: '';
  const mm = minute !== null ? `${String(minute).padStart(2, '0')}'+`: '';
  const hh = hour !== null ? `${String(hour).padStart(2, '0')}'+`: '';
  return `${hh}${mm}${ss}'`;
};

const buildScheduledTitle = ({ displayDate, displayTime, timeZoneLabel }) => {
  const parts = [];
  if (displayDate) parts.push(displayDate);
  if (displayTime) parts.push(`| ${displayTime}`);
  if (timeZoneLabel) parts.push(`/ ${timeZoneLabel}`);
  return parts.join(' ');
};

const TeamRow = ({ team, score }) => {
  const isUndefined = !team || !team.teamId;

  return (
    <div className={styles['match-card__team-row']}>
      <div className={styles['match-card__team']}>
        <div className={`${styles['match-card__flag']}${isUndefined ? ` ${styles['match-card__flag--undefined']}` : ''}`}>
          <img
            src={isUndefined ? PlaceholderEscudo : (team.flagUrl || PlaceholderBandera)}
            alt={isUndefined ? '' : team.name}
            className={styles['match-card__flag-image']}
            onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = PlaceholderBandera; }}
          />
        </div>
        <p className={`${styles['match-card__team-name']}${isUndefined ? ` ${styles['match-card__team-name--undefined']}` : ''}`}>
          {isUndefined ? 'Por definir' : team.name}
        </p>
      </div>
      <p className={styles['match-card__score']}>
        {score == null ? '–' : score}
      </p>
    </div>
  );
};

const MatchCard = ({ match, selectedTeamId, onMatchClick }) => {
  if (!match) return null;

  const {
    status,
    stage,
    group,
    displayDate,
    displayTime,
    timeZoneLabel,
    isWinner,
    isDraw,
    isLose,
    broadcast,
    matchProgress,
    homeTeam,
    awayTeam,
  } = match;
  const isCompleted = status === STATUS.COMPLETED;
  const isInProgress = status === STATUS.IN_PROGRESS;
  const isScheduled = status === STATUS.SCHEDULED;
  const isLive = isInProgress && broadcast?.isAvailable === true;

  const resultLetter = isCompleted
    ? getResultLetter({ isWinner, isDraw, isLose })
    : null;
  const resultVariant = isCompleted
    ? getResultVariant({ isWinner, isDraw, isLose })
    : null;

  const scheduledTitle = buildScheduledTitle({
    displayDate,
    displayTime: formatTime(displayTime),
    timeZoneLabel,
  });

  const liveTimeText = isInProgress ? formatLiveTime(matchProgress) : null;

  const selectedIsAway = selectedTeamId && awayTeam?.teamId === selectedTeamId;
  const team1 = selectedIsAway ? awayTeam : homeTeam;
  const team2 = selectedIsAway ? homeTeam : awayTeam;

  const homeBarColor = team1?.primaryHex ?? UNDEFINED_BAR_COLOR;
  const awayBarColor = team2?.primaryHex ?? UNDEFINED_BAR_COLOR;
  const cardClass = classNames(styles['match-card'], {
    [styles['match-card--live']]: isInProgress,
  });

  const handleClick = () => {
    if (onMatchClick) onMatchClick(match.matchId);
  };

  return (
    <article
      className={cardClass}
      onClick={handleClick}
      style={onMatchClick ? { cursor: 'pointer' } : undefined}
    >
      <div className={styles['match-card__top-bar']} aria-hidden="true">
        <div
          className={styles['match-card__top-bar-half']}
          style={{ backgroundColor: homeBarColor }}
        />
        <div
          className={styles['match-card__top-bar-half']}
          style={{ backgroundColor: awayBarColor }}
        />
      </div>

      <div className={styles['match-card__content']}>
        <header className={styles['match-card__header']}>
          <div className={styles['match-card__status-row']}>
            <span className={styles['match-card__status']}>
              {isCompleted && 'Finalizado'}
              {isInProgress && 'Minuto a minuto'}
              {isScheduled && scheduledTitle}
            </span>
            <div className={styles['match-card__status-right']}>
              {isLive && (
                <span className={styles['match-card__live-badge']}>
                  EN VIVO
                </span>
              )}
              {isInProgress && liveTimeText && (
                <span className={styles['match-card__live-time']}>
                  {liveTimeText}
                </span>
              )}
              {isCompleted && resultLetter && (
                <span className={classNames(
                  styles['match-card__result-badge'],
                  resultVariant && styles[`match-card__result-badge--${resultVariant}`],
                )}>
                  {resultLetter}
                </span>
              )}
            </div>
          </div>

          <p className={styles['match-card__date']}>
            {isCompleted ? scheduledTitle : ''}
          </p>

          {(stage || group) && (
            <div className={styles['match-card__meta']}>
              {stage && (
                <p className={styles['match-card__meta-line']}>{stage}</p>
              )}
              {group && (
                <p className={styles['match-card__meta-line']}>{group}</p>
              )}
            </div>
          )}
        </header>

        <div className={styles['match-card__divider']} aria-hidden="true" />

        <div className={styles['match-card__teams']}>
          <TeamRow team={team1} score={team1?.score} />
          <TeamRow team={team2} score={team2?.score} />
        </div>
      </div>
    </article>
  );
};

export default MatchCard;
